import { useRef } from 'react';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useCurrentLocation } from '../location/useCurrentLocation';
import { fetchFilterAggregates as fetchShopFilterAggregates } from '../shop/api';
import { useSessionId } from '../storage/useSessionId';

import { fetchFilterAggregates as fetchSearchFilterAggregates } from './api';
import {
  buildFilterAggregatesSearchQueryParams,
  buildShopFilterAggregatesSearchQueryParams,
  genericMenswearWomenswearGroupsStr,
} from './helpers';
import { useIsMenswearOrWomenswearCategoryPage } from './hooks/useIsMenswearOrWomenswearCategoryPage.ts';
import { buildFilterAggregatesQueryKey } from './queryKeys';
import { useSearchFilters } from './useSearchFilters';
import { useSearchInput } from './useSearchInput';

import { type AxiosCompatibleResponse } from '@/modules/http/types.ts';
import {
  type FetchFilterAggregatesResponse as SearchFetchFilterAggregatesResponse,
  SearchFrom,
} from '@/modules/search/types.ts';
import { type FetchFilterAggregatesResponse as ShopFetchFilterAggregatesResponse } from '@/modules/shop/types.ts';

type AggregatesResponse = AxiosCompatibleResponse<
  SearchFetchFilterAggregatesResponse | ShopFetchFilterAggregatesResponse
>;

/**
 * Hook to fetch filter aggregates data. Optimistically returns
 * the previous result if the search term has not changed to
 * help prevent layout shift.
 *
 * When a sellerId param is supplied, we make a request to the shop aggregates endpoint.
 * Otherwise, the search aggregates endpoint is used.
 */
export function useSearchFilterAggregates({ sellerId }: { sellerId?: number }) {
  const sessionId = useSessionId();
  const { location } = useCurrentLocation();
  const { searchFilters } = useSearchFilters();
  const { searchInput } = useSearchInput();
  const isShopSearch = Boolean(sellerId);
  const isMenswearOrWomenswearCategoryPage =
    useIsMenswearOrWomenswearCategoryPage();

  const previousResult = useRef<{
    searchInput: string | null;
    data: UseQueryResult<AggregatesResponse> | null;
  }>({ searchInput: null, data: null });

  const queryKey = buildFilterAggregatesQueryKey({
    location,
    searchInput,
    searchFilters,
    sellerId,
  });

  function queryFn() {
    if (isShopSearch) {
      return fetchShopFilterAggregates({
        sellerId: sellerId ?? '',
        params: buildShopFilterAggregatesSearchQueryParams({
          searchInput,
          searchFilters,
          location,
        }),
      });
    }
    return fetchSearchFilterAggregates({
      params: buildFilterAggregatesSearchQueryParams({
        searchInput,
        location,
        from: SearchFrom.InCountrySearch,
        searchFilters: isMenswearOrWomenswearCategoryPage
          ? {
              ...searchFilters,
              groups: genericMenswearWomenswearGroupsStr,
            }
          : searchFilters,
      }),
      meta: { sessionId: sessionId ?? '' },
    });
  }

  const queryData = useQuery<AggregatesResponse>({
    queryKey,
    queryFn,
  });

  /**
   * We retain the state of the previous result in order to
   * provide an optimistic result whilst the new aggregate query
   * is fetching.
   *
   * If the search input term has changed or if no previous
   * result has yet been cached, return the query as-is,
   * otherwise optimistically return the previous result.
   */
  if (!queryData.isLoading) {
    previousResult.current = {
      searchInput,
      data: queryData,
    };
    return queryData;
  }

  if (
    previousResult.current.searchInput !== searchInput ||
    !previousResult.current.data
  ) {
    return queryData;
  }

  return previousResult.current.data;
}
